import api from './api';
import { getApiUrl } from './config';
import { endpoint } from './endpoints';
import { handleApiCall } from '../utils/apiResponseHandler';
import type {
  UnifiedChatRequest,
  UnifiedChatResponse,
  ChatMessage,
  Carrier,
  DocumentIngestRequest,
  DocumentIngestResponse,
  UnderwritingQuestionRequest,
  UnderwritingQuestionResponse,
  AIAnalytics,
  DocumentUploadProgress
} from '../types/ai-unified';

export class UnifiedAIService {
  /**
   * Send a message to the unified AI system with SSE streaming
   * Calls onChunk for each text chunk received, returns full response when done
   */
  async sendMessageStream(
    request: UnifiedChatRequest,
    onChunk: (chunk: string) => void,
  ): Promise<UnifiedChatResponse['data']> {
    try {
      // Get auth token dynamically
      const { default: supabaseAuthService } = await import('./supabaseAuthService');
      const token = await supabaseAuthService.getAccessToken();

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(getApiUrl(endpoint.chatbot.unifiedMessageStream), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let fullResponse = '';
      let finalResult: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                fullResponse += data.content;
                onChunk(data.content);
              } else if (data.type === 'done') {
                finalResult = data;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              // Skip invalid JSON lines
            }
          }
        }
      }

      return finalResult || { response: fullResponse, sessionId: request.sessionId };
    } catch (error) {
      console.error('[UnifiedAI] Streaming error:', error);
      throw error;
    }
  }

  /**
   * Send a message to the unified AI system
   * Automatically routes between general AI and enhanced underwriting
   */
  async sendMessage(request: UnifiedChatRequest): Promise<UnifiedChatResponse['data']> {
    try {
      // handleApiCall extracts the data field from the backend response
      // So we get UnifiedChatResponse['data'] directly, not the full wrapped response
      const response = await handleApiCall<UnifiedChatResponse['data']>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.chatbot.unifiedMessage),
          body: request,
          auth: true,
        });
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get chat history for a session
   */
  async getChatHistory(sessionId: string): Promise<ChatMessage[]> {
    try {
      const response = await handleApiCall<any>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.chatbot.unifiedHistory(sessionId)),
          auth: true,
        });
      });

      // The response structure has messages nested inside
      // Extract messages from response.data.messages or response.messages
      let rawMessages: any[] = [];

      if (response?.data?.messages && Array.isArray(response.data.messages)) {
        rawMessages = response.data.messages;
      } else if (response?.messages && Array.isArray(response.messages)) {
        rawMessages = response.messages;
      } else if (Array.isArray(response?.data)) {
        rawMessages = response.data;
      } else if (Array.isArray(response)) {
        rawMessages = response;
      }

      // Client-rendered cards (e.g. the lead-details quote builder) are
      // persisted in session metadata — pull them out so they can be
      // re-attached to their message after the pairing below.
      const savedCards =
        response?.data?.metadata?.savedCards ?? response?.metadata?.savedCards;

      // Transform the messages to match the ChatMessage interface
      // Messages come as alternating user/bot, we need to pair them
      const transformedMessages: ChatMessage[] = [];

      for (let i = 0; i < rawMessages.length; i += 2) {
        const userMsg = rawMessages[i];
        const botMsg = rawMessages[i + 1];

        // Check both 'type' and 'role' fields for compatibility with backend
        const isUserMessage = userMsg && (userMsg.type === 'user' || userMsg.role === 'user');

        if (isUserMessage) {
          transformedMessages.push({
            id: `${sessionId}_${i}`,
            message: userMsg.content || '',
            response: botMsg?.content || '',
            timestamp: new Date(userMsg.timestamp || Date.now()),
            source: response?.contextType === 'underwriting' ? 'enhanced_underwriting' : 'general_ai',
            confidence: botMsg?.enhanced_metadata?.confidence_score || botMsg?.enhanced_metadata?.confidence,
            evidence_sources: botMsg?.enhanced_metadata?.evidence_sources,
            model_used: botMsg?.enhanced_metadata?.model_used,
          });
        }
      }

      // Re-attach persisted cards (savedCards entries are keyed by the
      // paired-message index) so e.g. the quote-builder form renders again
      // when a session is reopened from History.
      if (Array.isArray(savedCards)) {
        for (const entry of savedCards) {
          const idx = typeof entry?.messageIndex === "number" ? entry.messageIndex : 0;
          if (transformedMessages[idx] && Array.isArray(entry?.cards)) {
            transformedMessages[idx].cards = entry.cards;
          }
        }
      }

      return transformedMessages;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Persist a client-rendered exchange (no AI roundtrip) so it appears in
   * chat History — used by the lead-details quote flow, whose quote-builder
   * card is injected locally. Returns the created sessionId.
   */
  async saveCardSession(payload: {
    sessionId?: string;
    message: string;
    response: string;
    contextType?: string;
    cards?: any[];
    metadata?: Record<string, any>;
  }): Promise<{ sessionId: string } | null> {
    try {
      const response = await handleApiCall<any>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.chatbot.unifiedCardSession),
          data: payload,
          auth: true,
        });
      });
      const sessionId = response?.data?.sessionId ?? response?.sessionId;
      return sessionId ? { sessionId } : null;
    } catch (error) {
      // History persistence is best-effort — never block the quote flow on it.
      console.warn("[UnifiedAI] Failed to save card session:", error);
      return null;
    }
  }

  /**
   * Get available carriers for underwriting
   */
  async getCarriers(): Promise<Carrier[]> {
    try {
      const response = await handleApiCall<{ data: { carriers: Carrier[] } }>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.aiUnderwriting.getCarriers),
          auth: true,
        });
      });

      // Backend returns { data: { carriers: [...] } }
      const carriers = response.data?.carriers || [];


      return carriers;
    } catch (error) {
      console.error('[InsuraBot] Failed to load carriers:', error);
      // Return empty array but log the error for debugging
      return [];
    }
  }

  /**
   * Upload a document for underwriting ingestion
   */
  async uploadDocument(
    file: File,
    carrierSlug: string,
    onProgress?: (progress: DocumentUploadProgress) => void
  ): Promise<DocumentIngestResponse> {
    try {
      // First, upload the file to get a URL
      const uploadResponse = await this.uploadFileToStorage(file, onProgress);
      
      if (!uploadResponse.success) {
        throw new Error(uploadResponse.error || 'File upload failed');
      }

      // Then ingest the document
      // Format effective date in local timezone to avoid UTC shift
      const now = new Date();
      const effDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const ingestRequest: DocumentIngestRequest = {
        url: uploadResponse.url!,
        carrier_slug: carrierSlug,
        eff_date: effDate,
        version: '1.0',
        state_scope: ['ALL'],
        metadata: {
          uploaded_by: 'frontend_user',
          source: 'manual_upload'
        }
      };

      const response = await handleApiCall<DocumentIngestResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.aiUnderwriting.ingestDocument),
          body: ingestRequest,
          auth: true,
        });
      });

      return response;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Ask a specific underwriting question
   */
  async askUnderwritingQuestion(request: UnderwritingQuestionRequest): Promise<UnderwritingQuestionResponse> {
    try {
      const response = await handleApiCall<UnderwritingQuestionResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.aiUnderwriting.askQuestion),
          body: request,
          auth: true,
        });
      });

      return response;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get AI analytics
   */
  async getAnalytics(): Promise<AIAnalytics> {
    try {
      const response = await handleApiCall<{ data: AIAnalytics }>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.chatbot.unifiedAnalytics),
          auth: true,
        });
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get carrier documents
   */
  async getCarrierDocuments(carrierSlug: string): Promise<any[]> {
    try {
      const response = await handleApiCall<{ data: any[] }>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.aiUnderwriting.getCarrierDocuments(carrierSlug)),
          auth: true,
        });
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Private method to upload file to storage
   */
  private async uploadFileToStorage(
    file: File,
    onProgress?: (progress: DocumentUploadProgress) => void
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // Get signed URL for upload
      const signedUrlResponse = await api.post({
        url: getApiUrl(endpoint.upload.getSignedUrl),
        body: {
          fileName: file.name,
          fileType: file.type,
          bucketName: 'underwriting-documents'
        },
        auth: true,
      });

      if (!signedUrlResponse.data || !(signedUrlResponse.data as any).success) {
        throw new Error('Failed to get signed URL');
      }

      const { uploadUrl, fileId } = (signedUrlResponse.data as any).data;

      // Upload file to Supabase Storage
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      // Verify upload
      const verifyResponse = await api.post({
        url: getApiUrl(endpoint.upload.verifyHybrid),
        body: { fileId },
        auth: true,
      });

      if (!verifyResponse.data || !(verifyResponse.data as any).success) {
        throw new Error('Upload verification failed');
      }

      return {
        success: true,
        url: (verifyResponse.data as any).data.publicUrl
      };
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Detect if a message is underwriting-related
   */
  isUnderwritingQuestion(message: string): boolean {
    const underwritingKeywords = [
      'underwriting', 'eligibility', 'requirements', 'guidelines',
      'carrier', 'insurance', 'policy', 'coverage', 'premium',
      'age', 'health', 'medical', 'BMI', 'smoking', 'diabetes',
      'aetna', 'united healthcare', 'cigna', 'humana', 'anthem'
    ];

    const lowerMessage = message.toLowerCase();
    return underwritingKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  /**
   * Extract carrier from message
   */
  extractCarrierFromMessage(message: string): string | null {
    const carriers = [
      'aetna', 'united healthcare', 'cigna', 'humana', 'anthem',
      'blue cross', 'blue shield', 'kaiser', 'medicare', 'medicaid'
    ];

    const lowerMessage = message.toLowerCase();
    for (const carrier of carriers) {
      if (lowerMessage.includes(carrier)) {
        return carrier.replace(/\s+/g, '-');
      }
    }

    return null;
  }
}

export const unifiedAIService = new UnifiedAIService();
